"use client";

import React, {useState} from "react";
import _ from "lodash";

import {type IAssetSort, AssetSorts, AssetSortDirections} from "@isudio/data";
import {AssetSortIconTypes} from "@isudio/data";
import {Button, DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,} from "../index";

export interface SortDropdownProps {
  sorts: IAssetSort[];
  onChange?: (sort: IAssetSort) => void;
}

export function SortDropdown({
                               sorts = AssetSorts,
                               onChange,
                             }: SortDropdownProps) {
  const defaultSort: IAssetSort | undefined = _.find(sorts, {default: true});
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [selected, setSelected] = useState<IAssetSort | undefined>(defaultSort ?? sorts[0]);

  const onSortSelect = (sort: IAssetSort) => {
    setSelected(sort);
    onChange?.(sort);
  };

  const onDirectionSelect = (direction: AssetSortDirections) => {
    if (!selected) return;
    const newSelected = { ...selected, direction };
    setSelected(newSelected);
    onChange?.(newSelected);
  };

  return (
    <DropdownMenu onOpenChange={setIsSortOpen}>
      <DropdownMenuTrigger
        asChild>
        <Button
          variant="vanilla"
          className="h-8 px-2 gap-2 flex items-center border-none bg-bluegray-darker text-xs text-white rounded-[25px] cursor-pointer">
          <i className={`fa-duotone fa-light fa-arrow-down-${selected?.iconType === AssetSortIconTypes.numeric ? `${selected?.direction === AssetSortDirections.asc ? "1-9" : "9-1"}` : `${selected?.direction === AssetSortDirections.asc ? "a-z" : "z-a"}`} text-bluegray-lighter`}/>
          <span className="text-xs text-left font-medium text-white">{selected?.label}</span>
          <i className={`fa-duotone fa-light fa-chevron-${isSortOpen ? 'up' : 'down'} text-[10px] text-bluegray-lighter`}/>
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        className="w-30 bg-bluegray-darker rounded-[6px] shadow-[1px_1px_2px_1px_rgba(105,120,127,1)]!">

        <DropdownMenuLabel className="bg-bluegray-medium/50 text-bluegray-lighter font-extrabold text-[11px] tracking-wider px-2 py-1">Sort By</DropdownMenuLabel>
        {sorts.map((sort: IAssetSort) => (
          <DropdownMenuItem
            key={sort.field}
            onClick={() => onSortSelect(sort)}
            className={`flex justify-between text-xs transition duration-200 
              ${selected?.field === sort.field 
                ? 'text-white bg-bluegray-light/30' 
                : 'text-bluegray-lighter hover:text-generic-hover hover:bg-bluegray-light/12'}`}
          >
            {sort.label}
            {selected?.field === sort.field &&
              <i
                className="fa-duotone fa-light fa-check text-[10px] text-bluegray-lighter"/>}
          </DropdownMenuItem>
        ))}

        <DropdownMenuLabel className="bg-bluegray-medium/50 text-bluegray-lighter font-extrabold text-[11px] tracking-wider px-2 py-1">Order</DropdownMenuLabel>
        {Object.values(AssetSortDirections).map((direction) => (
          <DropdownMenuItem
            key={direction}
            onClick={() => onDirectionSelect(direction)}
            className={`flex justify-between text-xs transition duration-200 
            ${direction === selected?.direction 
              ? 'text-white bg-bluegray-light/30' 
              : 'text-bluegray-lighter hover:text-generic-hover hover:bg-bluegray-light/12'}`}
          >
            {direction === AssetSortDirections.asc ? "Ascending" : "Descending"}
            {direction === selected?.direction &&
              <i
                className="fa-duotone fa-light fa-check text-[10px] text-bluegray-lighter"/>}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
